import {
  MouseEvent,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
  createContext,
} from 'react';
import { Grid, Paper, Divider, Drawer, IconButton } from '@mui/material';
import SpectrumChart from 'features/spectrumChart/SpectrumChart';
import Spectrogram from 'features/spectrogram/Spectrogram';
import SingleTraceViewToolbar from './SingleTraceViewToolbar';
import MonitorConfigurationDialog from '../monitorConfiguration/MonitorConfigurationDialog';
import SpectralMaskConfigurationDialog from '../spectralMaskConfiguration/SpectralMaskConfigurationDialog';

import {
  updateMinMax,
  resetMinMax,
  selectTraceLength,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

import { useIsViewer } from '../auth/hooks/useIsViewer';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

import SettingsTabPanel from './SettingsTabPanel';
import { AnalyzerMode } from '../analyzers/types';
import useHandleCrossPolClicked from '../crosspol/hooks/useHandleCrossPolClicked';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import ThresholdConfigurationDialog from '../thresholds/ThresholdConfigurationDialog';
import { parseTraceFromBuffer } from 'utils/traceUtils';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { useClearMonitors } from 'features/monitors/api/clearMonitors';
import { useClearMasks } from 'features/spectralMasks/api/clearMasks';
import { useClearThresholds } from 'features/thresholds/api/clearThresholds';
import CenteredCircularProgress from 'components/CenteredCircularProgress';
import { MinMaxEnabled } from 'types';
import { useWall } from '../walls/api/getWall';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import { selectedMarkerChanged } from 'features/markers/markersSlice';
import { toggleSingleTraceView } from 'features/walls/wallsSlice';

interface Props {
  selectedAnalyzerId: number;
  selectedSnapshotId?: number;
  setSelectedSnapshotId?: Dispatch<SetStateAction<number>>;
  inDialog?: boolean;
  traceMode: number;
  handleDialogClose?: () => void;
  wallId?: number;
}

export const SingleTraceViewContext = createContext(null as any);

const SingleTraceView = ({
  selectedAnalyzerId,
  selectedSnapshotId,
  setSelectedSnapshotId,
  traceMode,
  inDialog,
  handleDialogClose,
  wallId,
}: Props) => {
  const store: any = useStore();
  const dispatch = useAppDispatch();
  const isViewer = useIsViewer();
  const { user } = useAppSelector((state) => state.auth);
  const { data: wall } = useWall({ wallId: wallId || -1 });

  const [crossPolSettings, setCrosspolSettings] = useState({
    peak_search: false,
  });

  const [currentDialog, setCurrentDialog] =
    useState<InterferenceDetector | null>(null);
  const [drawingMode, setDrawingMode] = useState<{
    isDrawing: boolean;
    drawingType: InterferenceDetector;
    redrawId: number;
    name?: string | undefined;
  }>({
    isDrawing: false,
    drawingType: InterferenceDetector.Monitor,
    redrawId: -1,
    name: undefined,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearDetector, setClearDetector] =
    useState<InterferenceDetector | null>(null);
  const drawerContainerRef = useRef<HTMLDivElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [drawerHeight, setDrawerHeight] = useState<number>(0);

  const selectedMarkerIndex = useAppSelector(
    (state) => state.markers.selectedMarker
  );

  useEffect(() => {
    if (selectedMarkerIndex > -1 && selectedMarkerIndex < 104) {
      setDrawingMode({
        isDrawing: true,
        drawingType: InterferenceDetector.Marker,
        redrawId: -1,
      });
    } else {
      setDrawingMode((prevState) => ({
        ...prevState,
        isDrawing: false,
      }));
    }
  }, [selectedMarkerIndex]);

  useEffect(() => {
    const handleResize = () => {
      if (!drawerContainerRef.current) return;
      if (drawerOpen) {
        setDrawerHeight(drawerContainerRef.current.clientHeight);
      } else {
        setDrawerHeight(0);
      }
    };
    handleResize();
    if (window) window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawerOpen, selectedSnapshotId]);

  const { data: selectedAnalyzer } = useAnalyzer({
    analyzerId: selectedAnalyzerId,
  });
  const { data: selectedSnapshot, error: snapshotError } = useSnapshot({
    snapshotId: selectedSnapshotId!,
    config: { keepPreviousData: true },
  });
  const updateSnapshotMutation = useUpdateSnapshot({});

  const clearMonitorsMutation = useClearMonitors({
    snapshotId: selectedSnapshotId || -1,
  });

  const clearThresholdsMutation = useClearThresholds({
    snapshotId: selectedSnapshotId || -1,
  });

  const clearMasksMutation = useClearMasks({
    snapshotId: selectedSnapshotId || -1,
  });

  useEffect(() => {
    if (!snapshotError) return;
    dispatch(toggleSingleTraceView(false));
  }, [snapshotError, dispatch]);

  useEffect(() => {
    if (!selectedSnapshot || selectedSnapshot?.id) return;
    if (
      selectedAnalyzer?.mode === AnalyzerMode.LIVE &&
      selectedSnapshot.crosspol_snapshot_id
    ) {
      updateSnapshotMutation.mutate({
        snapshotId: selectedSnapshot.id,
        data: { ...selectedSnapshot, crosspol_snapshot_id: null },
      });
    }
    //eslint-disable-next-line
  }, [selectedAnalyzer?.mode, selectedSnapshot, dispatch]);

  const memoizedSelectTraceLength = useMemo(selectTraceLength, []);

  let traceLength = useAppSelector(
    (state) =>
      selectedSnapshotId !== undefined &&
      memoizedSelectTraceLength(state, selectedSnapshotId)
  );

  if (!traceLength) traceLength = 601;

  const openMonitorDialog = useCallback(
    () => setCurrentDialog(InterferenceDetector.Monitor),
    []
  );
  const openThresholdDialog = useCallback(
    () => setCurrentDialog(InterferenceDetector.Threshold),
    []
  );
  const openMaskDialog = useCallback(
    () => setCurrentDialog(InterferenceDetector.Mask),
    []
  );
  const closeDialogs = useCallback(() => {
    setCurrentDialog(null);
  }, []);

  const handleBaselineClicked = async (event: MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    if (!selectedSnapshot) return;

    let currentTrace =
      store.getState().spectrasWebSocketServer?.traceData[selectedSnapshot.id]
        ?.trace;

    if (!currentTrace)
      currentTrace = parseTraceFromBuffer(selectedSnapshot.last_trace);

    const baselineTrace = !selectedSnapshot.baseline_enabled
      ? JSON.stringify(currentTrace)
      : null;

    const updatedSnapshot = {
      id: selectedSnapshot.id,
      analyzer_id: selectedSnapshot.analyzer_id,
      baseline_enabled: !selectedSnapshot.baseline_enabled,
      baseline_trace: baselineTrace,
    };

    await updateSnapshotMutation.mutateAsync({
      snapshotId: selectedSnapshot.id,
      data: updatedSnapshot,
    });
  };

  const handleMinClicked = async (event: MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    if (!selectedSnapshot || !user) return;
    const minMax = selectedSnapshot.minmax_enabled;

    let minMaxSetting;

    if (minMax === 1 || minMax === 3) {
      minMaxSetting = minMax - 1;
    } else {
      minMaxSetting = minMax + 1;
    }

    const updatedSnapshot = {
      id: selectedSnapshot.id,
      minmax_enabled: minMaxSetting as MinMaxEnabled,
    };

    await updateSnapshotMutation.mutateAsync({
      data: updatedSnapshot,
      snapshotId: updatedSnapshot.id,
    });

    if (selectedAnalyzer)
      dispatch(
        updateMinMax({
          user_id: user.id,
          properties: updatedSnapshot,
          analyzer: selectedAnalyzer,
        })
      );
  };

  const handleMaxClicked = async (event: MouseEvent<HTMLLIElement>) => {
    event.preventDefault();

    if (!selectedSnapshot || !user) return;
    const minMax = selectedSnapshot.minmax_enabled;

    let minMaxSetting;

    if (minMax === 2 || minMax === 3) {
      minMaxSetting = minMax - 2;
    } else {
      minMaxSetting = minMax + 2;
    }

    const updatedSnapshot = {
      id: selectedSnapshot.id,
      minmax_enabled: minMaxSetting as MinMaxEnabled,
    };

    await updateSnapshotMutation.mutateAsync({
      data: updatedSnapshot,
      snapshotId: updatedSnapshot.id,
    });

    if (selectedAnalyzer)
      dispatch(
        updateMinMax({
          user_id: user.id,
          properties: updatedSnapshot,
          analyzer: selectedAnalyzer,
        })
      );
  };

  const handleMinMaxResetClicked = () => {
    if (!selectedSnapshot || !user) return;
    dispatch(
      // @ts-ignore
      resetMinMax({
        user_id: user.id,
        analyzer: selectedAnalyzer,
        snapshot_id: selectedSnapshot.id,
      })
    );
  };

  const handleCrossPolClicked = useHandleCrossPolClicked(
    selectedSnapshotId,
    selectedAnalyzerId
  );

  const handleDrawInterferenceDetector = (
    detectorType: InterferenceDetector
  ) => {
    setDrawingMode({
      isDrawing: true,
      drawingType: detectorType,
      redrawId: -1,
    });
  };

  const handleRedrawClicked = (
    id: number,
    detectorType: InterferenceDetector,
    detectorName?: string
  ) => {
    closeDialogs();

    setDrawingMode({
      isDrawing: true,
      drawingType: detectorType,
      redrawId: id,
      name: detectorName,
    });
  };

  const handleDrawingFinished = () => {
    setDrawingMode({ ...drawingMode, isDrawing: false, redrawId: -1 });
    dispatch(selectedMarkerChanged(-1));
  };

  const handleClearClicked = (detectorType: InterferenceDetector) => {
    setClearDetector(detectorType);
    setDeleteDialogOpen(true);
  };

  const clearAlarmTriggers = async () => {
    switch (clearDetector) {
      case InterferenceDetector.Monitor:
        await clearMonitorsMutation.mutateAsync({
          snapshotId: selectedSnapshotId || -1,
        });
        break;
      case InterferenceDetector.Mask:
        await clearMasksMutation.mutateAsync({
          snapshotId: selectedSnapshotId || -1,
        });
        break;
      case InterferenceDetector.Threshold:
        await clearThresholdsMutation.mutateAsync({
          snapshotId: selectedSnapshotId || -1,
        });
        break;
      default:
        break;
    }
  };
  return (
    <SingleTraceViewContext.Provider
      value={{
        crossPolSettings,
        setCrosspolSettings,
      }}
    >
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={`${InterferenceDetector[clearDetector || 0]}s`}
        onSubmit={clearAlarmTriggers}
        onClose={() => setDeleteDialogOpen(false)}
      />
      {selectedSnapshotId !== undefined && traceLength && (
        <>
          <MonitorConfigurationDialog
            traceLength={traceLength}
            open={currentDialog === InterferenceDetector.Monitor}
            handleClose={closeDialogs}
            handleRedrawMonitor={handleRedrawClicked}
            selectedSnapshotId={selectedSnapshotId}
          />
          <ThresholdConfigurationDialog
            traceLength={traceLength}
            handleRedrawThreshold={handleRedrawClicked}
            open={currentDialog === InterferenceDetector.Threshold}
            handleClose={closeDialogs}
            selectedSnapshotId={selectedSnapshotId}
          />
          <SpectralMaskConfigurationDialog
            traceLength={traceLength}
            open={currentDialog === InterferenceDetector.Mask}
            handleClose={closeDialogs}
            handleRedrawMask={handleRedrawClicked}
            selectedSnapshotId={selectedSnapshotId}
          />
        </>
      )}
      <Grid
        container
        style={{
          minHeight: !inDialog ? 'calc(93vh - 64px)' : 'calc(80vh - 100px)',
        }}
      >
        <Grid
          style={{
            margin: 0,
            paddingTop: 0,
            boxShadow: inDialog ? '0px 0px 0px 0px #000' : undefined,
            position: 'relative',
          }}
          container
          item
          direction='column'
          component={Paper}
        >
          <IconButton
            sx={{
              position: 'absolute',
              right: 6,
              top: 6,
            }}
            onClick={() => setDrawerOpen((state) => !state)}
          >
            {drawerOpen ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
          <Grid item style={{ paddingTop: 0 }}>
            {traceMode === 0 && (
              <>
                <SingleTraceViewToolbar
                  analyzer={selectedAnalyzer}
                  snapshot={selectedSnapshot}
                  openMonitorDialog={openMonitorDialog}
                  openThresholdDialog={openThresholdDialog}
                  openMaskDialog={openMaskDialog}
                  handleDrawInterferenceDetector={
                    handleDrawInterferenceDetector
                  }
                  handleBaselineClicked={handleBaselineClicked}
                  handleMaxClicked={handleMaxClicked}
                  handleMinClicked={handleMinClicked}
                  handleMinMaxResetClicked={handleMinMaxResetClicked}
                  handleClearClicked={handleClearClicked}
                  handleCrossPolClicked={handleCrossPolClicked}
                  inDialog={inDialog}
                  handleDialogClose={handleDialogClose}
                />
                <Divider />
              </>
            )}
          </Grid>
          <Grid item container xs style={{ position: 'relative' }}>
            <Grid item xs />
            <Grid
              item
              xs={drawerOpen ? 6 : inDialog ? 8 : 12}
              lg={drawerOpen ? 7 : inDialog ? 9 : 12}
              xl={drawerOpen ? 8 : inDialog ? 10 : 10}
              style={{ paddingTop: 0 }}
            >
              {traceMode === 0 ? (
                <SpectrumChart
                  analyzerId={selectedAnalyzer!.id}
                  snapshotId={selectedSnapshotId!}
                  drawingMode={drawingMode}
                  drawingFinished={handleDrawingFinished}
                  hideDetectorNames={!!wall?.hide_detector_names}
                  crossPolPeakSearch={crossPolSettings.peak_search}
                />
              ) : (
                <div
                  style={{
                    padding: '.2rem',
                  }}
                >
                  <Spectrogram
                    analyzerId={selectedAnalyzer!.id}
                    snapshotId={selectedSnapshotId!}
                  />
                </div>
              )}
            </Grid>
            <Grid
              item
              xs={drawerOpen ? 6 : true}
              lg={drawerOpen ? 5 : true}
              xl={drawerOpen ? 4 : true}
              style={{ paddingTop: 0, position: 'relative' }}
              ref={drawerContainerRef}
            >
              <Drawer
                transitionDuration={0}
                anchor='right'
                variant='persistent'
                open={drawerOpen}
                sx={{
                  position: 'relative',
                  marginLeft: 'auto',
                  width: '100%',
                  '& .MuiBackdrop-root': {
                    display: 'none',
                  },
                  '& .MuiDrawer-paper': {
                    width: '100%',
                    position: 'absolute',
                    height: drawerHeight,
                    borderRadius: '10px',
                    border: 0,
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark' ? '#2d3640' : '#fff',
                  },
                }}
              >
                {selectedSnapshot ? (
                  <SettingsTabPanel
                    selectedAnalyzerId={selectedAnalyzerId}
                    selectedSnapshotId={selectedSnapshot.id}
                    setSelectedSnapshotId={setSelectedSnapshotId}
                    selectedSnapshot={selectedSnapshot}
                    displayActiveSnapshotSelector={inDialog ? false : true}
                    editSnapshotsDisabled={isViewer}
                    editMarkersDisabled={isViewer}
                    inDrawingMode={drawingMode.isDrawing}
                    inDialog={inDialog}
                    traceMode={traceMode}
                  />
                ) : (
                  <CenteredCircularProgress />
                )}
              </Drawer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </SingleTraceViewContext.Provider>
  );
};

export default SingleTraceView;
